import { useState, useEffect, useCallback } from 'react';
import { PrinterCapabilities, PrinterType, PrintOptions, ReceiptContent, ReceiptLine, PrintResult } from '@/types/thermal';
import { encodeText, encodeLine, encodeQrCode, encodeBarcode, encodeCut, encodeOpenDrawer, ESCPOS, LF } from '@/utils/escposEncoder';
import { printThermalContent } from '@/utils/thermalPrintUtils';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';

interface UseThermalPrinterReturn {
  capabilities: PrinterCapabilities;
  isReady: boolean;
  isNative: boolean;
  printText: (text: string, options?: PrintOptions) => Promise<PrintResult>;
  printLine: (char?: string) => Promise<PrintResult>;
  printQrCode: (data: string, size?: number) => Promise<PrintResult>;
  printBarcode: (code: string, type?: 'EAN13' | 'CODE128' | 'CODE39') => Promise<PrintResult>;
  feedPaper: (lines?: number) => Promise<void>;
  cutPaper: () => Promise<void>;
  openCashDrawer: () => Promise<void>;
  printReceipt: (content: ReceiptContent) => Promise<PrintResult>;
  printViaWeb: (htmlContent: string) => void;
}

/**
 * Detect printer type based on environment
 */
function detectPrinterType(): PrinterType {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for specific Smart POS terminals
  if (userAgent.includes('sunmi')) return 'sunmi';
  if (userAgent.includes('stone') || userAgent.includes('stonepago')) return 'stone';
  if (userAgent.includes('positivo') || userAgent.includes('gertec')) return 'positivo';
  if (userAgent.includes('pax')) return 'pax';
  
  // Check if running in Capacitor Android (potential generic Smart POS)
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    // Check for Sunmi printer interface
    if (window.SunmiInnerPrinter) return 'sunmi';
    // Check for Android WebView bridge
    if (window.Android) return 'generic';
    return 'generic';
  }
  
  return 'web';
}

/**
 * Get printer capabilities based on type
 */
function getCapabilities(printerType: PrinterType): PrinterCapabilities {
  const baseCapabilities: PrinterCapabilities = {
    hasNativePrinter: printerType !== 'web',
    printerType,
    supportsQrCode: true,
    supportsBarcode: true,
    paperWidth: 80,
    supportsCut: printerType !== 'web',
    supportsCashDrawer: printerType === 'sunmi' || printerType === 'generic',
  };

  // Adjust for specific terminals
  if (printerType === 'stone') {
    baseCapabilities.paperWidth = 58;
    baseCapabilities.supportsCashDrawer = false;
  }

  return baseCapabilities;
}

/**
 * Hook for thermal printer operations with intelligent fallback chain
 * Fallback order: Sunmi JSInterface → Stone Intent → Generic Android Intent → Web Print
 */
export function useThermalPrinter(): UseThermalPrinterReturn {
  const [printerType, setPrinterType] = useState<PrinterType>('web');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const detected = detectPrinterType();
    setPrinterType(detected);
    setIsReady(true);
    
    logger.log('[ThermalPrinter] Detected printer type:', detected);
    
    // Initialize Sunmi printer if available
    if (detected === 'sunmi' && window.SunmiInnerPrinter) {
      try {
        window.SunmiInnerPrinter.printerInit();
        logger.log('[ThermalPrinter] Sunmi printer initialized');
      } catch (error) {
        logger.warn('[ThermalPrinter] Failed to initialize Sunmi printer:', error);
      }
    }
  }, []);

  const capabilities = getCapabilities(printerType);
  const isNative = printerType !== 'web';

  /**
   * Print via Sunmi JSInterface
   */
  const printViaSunmi = async (content: string): Promise<PrintResult> => {
    return new Promise((resolve) => {
      if (!window.SunmiInnerPrinter) {
        resolve({ success: false, error: 'Sunmi printer not available', method: 'sunmi' });
        return;
      }

      try {
        window.SunmiInnerPrinter.printerInit();
        window.SunmiInnerPrinter.printText(content, () => {
          window.SunmiInnerPrinter?.lineWrap(3, () => {
            resolve({ success: true, method: 'sunmi' });
          });
        });
      } catch (error) {
        resolve({ 
          success: false, 
          error: error instanceof Error ? error.message : 'Sunmi print failed',
          method: 'sunmi' 
        });
      }
    });
  };

  /**
   * Print via Stone Intent (br.com.stone.pos.action.PRINT)
   */
  const printViaStoneIntent = async (content: string): Promise<PrintResult> => {
    return new Promise((resolve) => {
      try {
        // Stone POS uses a specific intent for printing
        const intent = {
          action: 'br.com.stone.pos.action.PRINT',
          extras: {
            printContent: content,
            printType: 'text',
          },
        };

        if (window.Android?.startActivity) {
          window.Android.startActivity(JSON.stringify(intent));
          resolve({ success: true, method: 'stone_intent' });
        } else {
          resolve({ success: false, error: 'Stone intent not available', method: 'stone_intent' });
        }
      } catch (error) {
        resolve({ 
          success: false, 
          error: error instanceof Error ? error.message : 'Stone intent failed',
          method: 'stone_intent' 
        });
      }
    });
  };

  /**
   * Print via generic Android Intent
   */
  const printViaAndroidIntent = async (content: string): Promise<PrintResult> => {
    return new Promise((resolve) => {
      try {
        if (window.Android?.print) {
          window.Android.print(content);
          resolve({ success: true, method: 'android_intent' });
        } else {
          resolve({ success: false, error: 'Android print not available', method: 'android_intent' });
        }
      } catch (error) {
        resolve({ 
          success: false, 
          error: error instanceof Error ? error.message : 'Android print failed',
          method: 'android_intent' 
        });
      }
    });
  };

  /**
   * Print via web (window.print)
   */
  const printViaWeb = (htmlContent: string): void => {
    printThermalContent(htmlContent, { autoPrint: true });
  };

  /**
   * Intelligent fallback printing chain
   */
  const printWithFallback = async (content: string, htmlContent?: string): Promise<PrintResult> => {
    // Try Sunmi first
    if (window.SunmiInnerPrinter) {
      const result = await printViaSunmi(content);
      if (result.success) return result;
      logger.warn('[ThermalPrinter] Sunmi failed, trying next...');
    }

    // Try Stone Intent
    if (printerType === 'stone' || Capacitor.getPlatform() === 'android') {
      const result = await printViaStoneIntent(content);
      if (result.success) return result;
      logger.warn('[ThermalPrinter] Stone intent failed, trying next...');
    }

    // Try generic Android
    if (window.Android?.print) {
      const result = await printViaAndroidIntent(content);
      if (result.success) return result;
      logger.warn('[ThermalPrinter] Android intent failed, falling back to web...');
    }

    // Fallback to web print
    if (htmlContent) {
      printViaWeb(htmlContent);
      return { success: true, method: 'web' };
    }

    // Convert plain text to HTML for web
    const simpleHtml = `<pre style="font-family: 'Courier New', monospace; font-size: 12px; white-space: pre-wrap;">${content}</pre>`;
    printViaWeb(simpleHtml);
    return { success: true, method: 'web' };
  };

  /**
   * Print text with options
   */
  const printText = useCallback(async (text: string, options?: PrintOptions): Promise<PrintResult> => {
    const encoded = encodeText(text, options);
    return printWithFallback(encoded);
  }, [printerType]);

  /**
   * Print a divider line
   */
  const printLine = useCallback(async (char: string = '-'): Promise<PrintResult> => {
    const encoded = encodeLine(char, capabilities.paperWidth === 80 ? 48 : 32);
    return printWithFallback(encoded);
  }, [capabilities.paperWidth, printerType]);

  /**
   * Print QR code
   */
  const printQrCode = useCallback(async (data: string, size: number = 6): Promise<PrintResult> => {
    if (window.SunmiInnerPrinter) {
      return new Promise((resolve) => {
        try {
          window.SunmiInnerPrinter!.setAlignment(1, () => {
            window.SunmiInnerPrinter!.printQRCode(data, size, 3, () => {
              window.SunmiInnerPrinter!.lineWrap(2, () => {
                resolve({ success: true, method: 'sunmi' });
              });
            });
          });
        } catch (error) {
          resolve({ 
            success: false, 
            error: error instanceof Error ? error.message : 'QR print failed',
            method: 'sunmi' 
          });
        }
      });
    }
    
    // Fallback: use ESC/POS encoded QR
    const encoded = ESCPOS.ALIGN_CENTER + encodeQrCode(data, size) + LF;
    return printWithFallback(encoded);
  }, [printerType]);

  /**
   * Print barcode
   */
  const printBarcode = useCallback(async (
    code: string, 
    type: 'EAN13' | 'CODE128' | 'CODE39' = 'CODE128'
  ): Promise<PrintResult> => {
    if (window.SunmiInnerPrinter) {
      return new Promise((resolve) => {
        try {
          const symbology = type === 'EAN13' ? 2 : type === 'CODE128' ? 8 : 4;
          window.SunmiInnerPrinter!.setAlignment(1, () => {
            window.SunmiInnerPrinter!.printBarCode(code, symbology, 100, 2, 2, () => {
              window.SunmiInnerPrinter!.lineWrap(2, () => {
                resolve({ success: true, method: 'sunmi' });
              });
            });
          });
        } catch (error) {
          resolve({ 
            success: false, 
            error: error instanceof Error ? error.message : 'Barcode print failed',
            method: 'sunmi' 
          });
        }
      });
    }
    
    const encoded = ESCPOS.ALIGN_CENTER + encodeBarcode(code, type) + LF;
    return printWithFallback(encoded);
  }, [printerType]);

  /**
   * Feed paper
   */
  const feedPaper = useCallback(async (lines: number = 3): Promise<void> => {
    if (window.SunmiInnerPrinter) {
      window.SunmiInnerPrinter.lineWrap(lines);
    }
  }, []);

  /**
   * Cut paper
   */
  const cutPaper = useCallback(async (): Promise<void> => {
    if (window.SunmiInnerPrinter) {
      window.SunmiInnerPrinter.cutPaper();
    }
  }, []);

  /**
   * Open cash drawer
   */
  const openCashDrawer = useCallback(async (): Promise<void> => {
    if (window.SunmiInnerPrinter) {
      window.SunmiInnerPrinter.openDrawer();
    } else if (window.Android?.openCashDrawer) {
      window.Android.openCashDrawer();
    }
  }, []);

  /**
   * Print a complete receipt from structured content
   */
  const printReceipt = useCallback(async (content: ReceiptContent): Promise<PrintResult> => {
    // Build plain text content
    let textContent = ESCPOS.INIT;
    let htmlContent = '<div class="receipt">';

    for (const line of content.lines) {
      switch (line.type) {
        case 'text':
          textContent += encodeText(line.content || '', line.options);
          htmlContent += buildHtmlLine(line);
          break;
        case 'line':
          textContent += encodeLine(line.content || '-');
          htmlContent += `<div class="divider">${(line.content || '-').repeat(48)}</div>`;
          break;
        case 'qrcode':
          textContent += ESCPOS.ALIGN_CENTER + encodeQrCode(line.content || '', line.size || 4) + LF;
          htmlContent += `<div class="qrcode-placeholder text-center">[QR: ${line.content}]</div>`;
          break;
        case 'barcode':
          textContent += ESCPOS.ALIGN_CENTER + encodeBarcode(line.content || '', line.barcodeType) + LF;
          htmlContent += `<div class="barcode-placeholder text-center">[Barcode: ${line.content}]</div>`;
          break;
        case 'space':
          textContent += '\n'.repeat(line.size || 1);
          htmlContent += `<div style="height: ${(line.size || 1) * 12}px"></div>`;
          break;
      }
    }

    htmlContent += '</div>';

    // Print with fallback
    const result = await printWithFallback(textContent, htmlContent);

    // Handle post-print actions
    if (result.success) {
      if (content.cutPaper && capabilities.supportsCut) {
        await cutPaper();
      }
      if (content.openDrawer && capabilities.supportsCashDrawer) {
        await openCashDrawer();
      }
    }

    return result;
  }, [capabilities, cutPaper, openCashDrawer, printerType]);

  return {
    capabilities,
    isReady,
    isNative,
    printText,
    printLine,
    printQrCode,
    printBarcode,
    feedPaper,
    cutPaper,
    openCashDrawer,
    printReceipt,
    printViaWeb,
  };
}

/**
 * Build HTML line from receipt line
 */
function buildHtmlLine(line: ReceiptLine): string {
  const styles: string[] = [];
  
  if (line.options?.align === 'center') styles.push('text-align: center');
  if (line.options?.align === 'right') styles.push('text-align: right');
  if (line.options?.bold) styles.push('font-weight: bold');
  if (line.options?.size === 2) styles.push('font-size: 14px');
  if (line.options?.size === 3) styles.push('font-size: 16px');
  
  const styleAttr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
  
  return `<div${styleAttr}>${line.content || ''}</div>`;
}
